// src/pages/ResultsPage.js
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { collection, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { dimensions, interpretations, recommendations } from '../data/surveyData';
import GaugeChart from 'react-gauge-chart';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { FaDownload, FaUsers, FaChartBar, FaLightbulb, FaSync } from 'react-icons/fa';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const ResultsPage = () => {
  const { surveyId } = useParams();
  const [surveyData, setSurveyData] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Fetch survey data
  useEffect(() => {
    const fetchSurveyData = async () => {
      try {
        const q = query(collection(db, 'surveys'), where('id', '==', surveyId));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          setSurveyData(querySnapshot.docs[0].data());
        }
      } catch (error) {
        console.error('Error fetching survey:', error);
      }
    };

    fetchSurveyData();
  }, [surveyId]);

  // Listen to responses in real-time
  useEffect(() => {
    const q = query(collection(db, 'responses'), where('surveyId', '==', surveyId));
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const responsesData = [];
      querySnapshot.forEach((doc) => {
        responsesData.push({ id: doc.id, ...doc.data() });
      });
      setResponses(responsesData);
      calculateResults(responsesData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [surveyId]);

  const calculateResults = (responsesData) => {
    if (responsesData.length === 0) {
      setResults(null);
      return;
    }

    // Calculate overall average
    const overallSum = responsesData.reduce((sum, r) => sum + r.overallScore, 0);
    const overallAvg = overallSum / responsesData.length;

    // Calculate dimension averages
    const dimensionAverages = {};
    dimensions.forEach(dim => {
      const sum = responsesData.reduce((s, r) => s + (r.dimensionScores[dim.id] || 0), 0);
      dimensionAverages[dim.id] = sum / responsesData.length;
    });

    // Calculate eNPS
    const promoters = responsesData.filter(r => r.eNPS >= 9).length;
    const detractors = responsesData.filter(r => r.eNPS <= 6).length;
    const eNPSScore = ((promoters - detractors) / responsesData.length) * 100;

    // Find interpretation
    let interpretation = null;
    for (const [key, value] of Object.entries(interpretations)) {
      if (overallAvg >= value.range[0] && overallAvg <= value.range[1]) {
        interpretation = value;
        break;
      }
    }

    setResults({
      overallScore: overallAvg,
      dimensionScores: dimensionAverages,
      eNPS: eNPSScore,
      responseCount: responsesData.length,
      interpretation
    });
  };

  const getRecommendationsForDimension = (dimId, score) => {
    const dimKey = dimId;
    if (score < 3.0 && recommendations[dimKey]?.weak) {
      return recommendations[dimKey].weak;
    } else if (score >= 4.0 && recommendations[dimKey]?.strong) {
      return recommendations[dimKey].strong;
    }
    return [];
  };

  const generatePDF = async () => {
    const element = document.getElementById('results-report');
    const canvas = await html2canvas(element, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    
    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgWidth = 210;
    const pageHeight = 297;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save(`happiness-report-${surveyId}.pdf`);
  };

  const refreshData = () => {
    setRefreshing(true);
    // Data will refresh automatically due to onSnapshot
    setTimeout(() => setRefreshing(false), 1000);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
          <FaUsers className="text-5xl text-yellow-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-yellow-800 mb-2">
            في انتظار المشاركات
          </h2>
          <p className="text-yellow-700 mb-4">
            لم يقم أي موظف بتعبئة الاستبيان حتى الآن.
          </p>
          <p className="text-gray-600">
            شارك رابط الاستبيان مع موظفيك: 
            <br />
            <code className="bg-gray-100 px-2 py-1 rounded mt-2 inline-block" dir="ltr">
              {window.location.origin}/survey/{surveyId}
            </code>
          </p>
        </div>
      </div>
    );
  }

  // Prepare data for charts
  const barChartData = dimensions.map(dim => ({
    name: dim.name,
    النتيجة: results.dimensionScores[dim.id].toFixed(2),
    color: dim.color
  }));

  const radarChartData = dimensions.map(dim => ({
    dimension: dim.name,
    score: results.dimensionScores[dim.id],
    fullMark: 5
  }));

  // Sort dimensions by score
  const sortedDimensions = [...dimensions].sort(
    (a, b) => results.dimensionScores[b.id] - results.dimensionScores[a.id]
  );

  const topDimensions = sortedDimensions.slice(0, 3);
  const bottomDimensions = sortedDimensions.slice(-3);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div id="results-report">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            تقرير السعادة الوظيفية
          </h1>
          {surveyData && (
            <p className="text-xl text-gray-600">
              {surveyData.organizationName}
            </p>
          )}
          <p className="text-gray-500 mt-2">
            تاريخ التقرير: {new Date().toLocaleDateString('ar-SA')}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4 mb-6 no-print">
          <button
            onClick={refreshData}
            className="flex items-center px-4 py-2 bg-primary-gold hover:bg-yellow-700 text-white rounded-lg transition-colors"
            disabled={refreshing}
          >
            <FaSync className={`ml-2 ${refreshing ? 'animate-spin' : ''}`} />
            تحديث البيانات
          </button>
          <button
            onClick={generatePDF}
            className="flex items-center px-4 py-2 bg-primary-green hover:bg-green-700 text-white rounded-lg transition-colors"
          >
            <FaDownload className="ml-2" />
            تحميل PDF
          </button>
        </div>

        {/* Main Score Card */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                مؤشر السعادة الكلي
              </h2>
              <div className="gauge-container">
                <GaugeChart
                  id="gauge-chart"
                  nrOfLevels={5}
                  arcsLength={[0.2, 0.2, 0.2, 0.2, 0.2]}
                  colors={['#E74C3C', '#FFA726', '#F9A825', '#00897B', '#00695C']}
                  percent={results.overallScore / 5}
                  arcPadding={0.02}
                  textColor="#424242"
                  formatTextValue={() => `${(results.overallScore * 20).toFixed(0)}%`}
                  needleColor="#424242"
                  needleBaseColor="#424242"
                />
              </div>
              {results.interpretation && (
                <div 
                  className="mt-4 px-6 py-3 rounded-lg inline-block"
                  style={{ backgroundColor: `${results.interpretation.color}20` }}
                >
                  <p className="font-bold text-xl" style={{ color: results.interpretation.color }}>
                    {results.interpretation.label}
                  </p>
                  <p className="text-gray-700 text-sm mt-1">
                    {results.interpretation.description}
                  </p>
                </div>
              )}
            </div>

            <div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <FaUsers className="text-3xl text-primary-green mx-auto mb-2" />
                  <p className="text-2xl font-bold text-primary-darkGray">
                    {results.responseCount}
                  </p>
                  <p className="text-primary-green text-sm">موظف شارك</p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4 text-center">
                  <FaChartBar className="text-3xl text-primary-gold mx-auto mb-2" />
                  <p className="text-2xl font-bold text-primary-darkGray">
                    {results.eNPS.toFixed(0)}
                  </p>
                  <p className="text-primary-gold text-sm">نقاط eNPS</p>
                </div>
              </div>

              <div className="mt-6">
                <h3 className="font-bold text-gray-900 mb-3">نقاط القوة:</h3>
                <ul className="space-y-2">
                  {topDimensions.map(dim => (
                    <li key={dim.id} className="flex items-center">
                      <div 
                        className="w-4 h-4 rounded-full ml-2"
                        style={{ backgroundColor: dim.color }}
                      />
                      <span className="text-gray-700">
                        {dim.name} ({results.dimensionScores[dim.id].toFixed(2)}/5)
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="font-bold text-gray-900 mb-3">تحتاج تحسين:</h3>
                <ul className="space-y-2">
                  {bottomDimensions.map(dim => (
                    <li key={dim.id} className="flex items-center">
                      <div 
                        className="w-4 h-4 rounded-full ml-2"
                        style={{ backgroundColor: dim.color }}
                      />
                      <span className="text-gray-700">
                        {dim.name} ({results.dimensionScores[dim.id].toFixed(2)}/5)
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Dimension Scores */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          {/* Bar Chart */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              النتائج حسب المحور
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={barChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                <YAxis domain={[0, 5]} />
                <Tooltip />
                <Bar dataKey="النتيجة" fill="#3498DB" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Radar Chart */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              التحليل الشامل
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={radarChartData}>
                <PolarGrid />
                <PolarAngleAxis dataKey="dimension" />
                <PolarRadiusAxis angle={90} domain={[0, 5]} />
                <Radar 
                  name="النتيجة" 
                  dataKey="score" 
                  stroke="#3498DB" 
                  fill="#3498DB" 
                  fillOpacity={0.6} 
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recommendations */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <FaLightbulb className="text-yellow-500 ml-3" />
            التوصيات الرئيسية
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {dimensions.map(dim => {
              const score = results.dimensionScores[dim.id];
              const recs = getRecommendationsForDimension(dim.id, score);
              
              if (recs.length === 0) return null;
              
              return (
                <div key={dim.id} className="border rounded-lg p-4">
                  <div className="flex items-center mb-3">
                    <div 
                      className="w-4 h-4 rounded-full ml-2"
                      style={{ backgroundColor: dim.color }}
                    />
                    <h3 className="font-bold text-gray-900">
                      {dim.name}
                    </h3>
                    <span className="mr-auto text-sm text-gray-600">
                      ({score.toFixed(2)}/5)
                    </span>
                  </div>
                  <ul className="space-y-2">
                    {recs.slice(0, 3).map((rec, idx) => (
                      <li key={idx} className="text-sm text-gray-700 flex items-start">
                        <span className="text-gray-400 ml-2">•</span>
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          {/* Action Plan */}
          <div className="mt-8 bg-blue-50 rounded-lg p-6">
            <h3 className="font-bold text-blue-900 mb-3">
              خطة العمل المقترحة:
            </h3>
            <ol className="list-decimal list-inside text-blue-800 space-y-2">
              <li>مناقشة النتائج مع فريق الإدارة</li>
              <li>مشاركة النتائج الإيجابية مع الموظفين</li>
              <li>وضع خطة تحسين للمحاور الضعيفة خلال أسبوعين</li>
              <li>تنفيذ مبادرات سريعة المكسب خلال شهر</li>
              <li>إعادة القياس بعد 6 أشهر لمتابعة التقدم</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultsPage;
